/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import './TrustedContactsModal.scss';

import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import { bool, func, object, string } from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import TrustedContactsGlobal from 'components/layouts/Preferences/TrustedContacts/TrustedContactsModal/TrustedContactsGlobal';
import ContactInformation from 'components/layouts/Preferences/TrustedContacts/TrustedContactsModal/TrustedContactsModalInformation';
import OtherInformation from 'components/layouts/Preferences/TrustedContacts/TrustedContactsModal/TrustedContactsModalMisc';
import AccountDisplay from 'components/shared/AccountDisplay/AccountDisplay';
import BaseModal from 'components/shared/Modal/BaseModal';
import { trustedContactInitialState } from 'state/preferences/PreferencesReducer';
import { getCustomerTrustedContacts } from 'state/customer/CustomerSelectors';
import LinkButton from 'components/shared/Buttons/LinkButton';
import ModalLoadingSpinner from 'components/shared/Modal/ModalLoadingSpinner';
import {
  appAddressToComponentAddressMapper,
  applyAccountNumbersToContacts,
  appPhoneNumberToComponentPhoneNumberMapper,
  componentPhoneNumberToAppPhoneNumberMapper,
  componentTrustedContactsToAppTrustedContactsMapper,
  createNewAddressFromComponentAddress,
  defaultEmailAddressAppState,
  defaultMailingAddressAppState
} from 'services/Preferences';
import { getTrustedContactMappedGroups } from 'state/preferences/PreferencesSelectors';
import {
  getClientMissions,
  updateCustomerPreferences
} from 'services/Rest';
import Store from 'state/Store';
import USAddress from 'components/layouts/Preferences/UpdateContactInformation/MailingAddress/USAddress';
import {
  clearInvalidFields,
  clearTouchedFields
} from 'state/validation/ValidationActions';
import { isMobileLayout } from 'state/view/ViewSelectors';
import TrustedContactsDeleteModal from '../TrustedContactsTableActionModal/TrustedContactsDeleteModal';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import { errorUpdateTrustedContacts } from 'state/preferences/PreferencesActions';
import { checkAllFieldsValid, trustedContactAddressValidation } from 'services/Validation';
import { clientMissionsEnabled, isTrustedContactsAccountNumberEnabled } from 'services/FeatureFlags';
import { getAccountsById } from 'state/account/AccountSelectors';

export const TrustedContactButton = ({ className, disable, onClick, text }) => (
  <button className={className} disabled={disable} onClick={onClick}>
    {text}
  </button>
);

export const TrustedContactsButtonWithPermissions =
  WithUserPermissions(TrustedContactButton);

class TrustedContactsModal extends React.Component {
  state = {
    accountIdMap: this.buildAccountIdMap(),
    accountIdTrustedContactsMap: this.buildAccountIdTrustedContactsMap(),
    showModal: false,
    showDelete: false,
    showTrustedContactsGlobal: false,
    updatingContacts: false,
    ...this.copyBffState()
  };

  componentDidUpdate(prevProps) {
    const { requestStatus } = this.props;
    if (
      isEmpty(prevProps.trustedContactMappedGroups) &&
      !isEmpty(this.props.trustedContactMappedGroups)
    ) {
      this.setState({
        accountIdMap: this.buildAccountIdMap(),
        accountIdTrustedContactsMap: this.buildAccountIdTrustedContactsMap()
      });
    }

    if (
      prevProps.requestStatus['updateCustomerPreferences'] !==
      requestStatus['updateCustomerPreferences']
    ) {
      if (
        ['RECEIVED', 'ERROR'].includes(
          requestStatus['updateCustomerPreferences']
        )
      ) {
        this.resetState();
      }
    }
  }

  closeModal = () => {
    const { touchedFields } = this.props;
    if (!isEmpty(touchedFields)) {
      const result = window.confirm(
        'Your changes will not be saved if you proceed.'
      );
      if (result) {
        this.resetState();
      }
    } else {
      this.resetState();
    }
  };

  handleBackClick = () => {
    let { accountIdMap } = this.state;
    Object.keys(accountIdMap).forEach(
      (accountId) => (accountIdMap[accountId] = false)
    );
    const accountIdTrustedContactsMap = this.buildAccountIdTrustedContactsMap();
    this.setState({
      accountIdMap,
      accountIdTrustedContactsMap,
      showTrustedContactsGlobal: false
    });
  };

  handleCancelClick = () => {
    this.closeModal();
  };

  handleClearAll = () => {
    let { accountIdMap } = this.state;
    Object.keys(accountIdMap).forEach(
      (accountId) => (accountIdMap[accountId] = false)
    );
    this.setState({ accountIdMap });
  };

  handleNextClick = () => {
    const { name: { accountId } = {} } = this.state;
    let { accountIdMap } = this.state;

    if (accountId) {
      accountIdMap[accountId] = true;
      this.setState({
        accountIdMap,
        showTrustedContactsGlobal: true
      });
    } else {
      this.setState({
        showTrustedContactsGlobal: true
      });
    }
  };

  handleSelectAll = () => {
    let { accountIdMap } = this.state;
    Object.keys(accountIdMap).forEach(
      (accountId) => (accountIdMap[accountId] = true)
    );
    this.setState({ accountIdMap });
  };

  handleSaveTrustedContacts = () => {
    const { customerTrustedContacts, isAddView, accountsById } = this.props;
    let updatedAllTrustedContacts = cloneDeep(customerTrustedContacts);
    const {
      accountIdMap,
      accountIdTrustedContactsMap,
      emailAddress,
      firstName,
      lastName,
      name: { accountId } = {},
      phoneNumbers,
      relationship,
      sequenceNumber
    } = this.state;

    const mailingAddress = createNewAddressFromComponentAddress({
      ...this.state.mailingAddress
    });
    const phoneNumber =
      componentPhoneNumberToAppPhoneNumberMapper(phoneNumbers)[0];

    // There are two courses of action, an add path (addView) or edit path (editView).
    if (isAddView) {
      let selectedAccountIds = [];
      Object.keys(accountIdMap).forEach((accountId) => {
        if (accountIdMap[accountId]) {
          selectedAccountIds.push(accountId);
        }
      });

      selectedAccountIds.forEach((accountId) => {
        let disposition;
        const { sequenceNumber } =
          accountIdTrustedContactsMap[accountId].find(({ active }) => active) ||
          {};

        if (sequenceNumber === null) {
          disposition = { action: 'add' };
        } else {
          disposition = { action: 'update' };
          const index = updatedAllTrustedContacts.findIndex((contact) => {
            return (
              contact.accountId == accountId &&
              contact.sequenceNumber === sequenceNumber
            );
          });
          if (index > -1) {
            updatedAllTrustedContacts.splice(index, 1);
          }
        }

        const trustedContact =
          componentTrustedContactsToAppTrustedContactsMapper(
            {
              accountId,
              firstName,
              lastName,
              phoneNumber: {
                ...phoneNumber,
                disposition: null,
                countryCode: '1'
              },
              mailingAddress: { ...mailingAddress, contactId: 0 },
              emailAddress,
              relationship,
              sequenceNumber
            },
            disposition
          );
        updatedAllTrustedContacts.push(trustedContact);
      });
    } else {
      const disposition = { action: 'update' };
      const index = updatedAllTrustedContacts.findIndex((contact) => {
        return (
          contact.accountId == accountId &&
          contact.sequenceNumber === sequenceNumber
        );
      });
      if (index > -1) {
        const trustedContact =
          componentTrustedContactsToAppTrustedContactsMapper(
            {
              accountId,
              firstName,
              lastName,
              phoneNumber: {
                ...phoneNumber,
                disposition: null,
                countryCode: '1'
              },
              mailingAddress: { ...mailingAddress, contactId: 0 },
              emailAddress,
              relationship,
              sequenceNumber
            },
            disposition
          );
        updatedAllTrustedContacts.splice(index, 1, trustedContact);
      }
    }
    if(isTrustedContactsAccountNumberEnabled()) {
      updatedAllTrustedContacts = applyAccountNumbersToContacts(updatedAllTrustedContacts, accountsById)
    }
    updateCustomerPreferences(
      {trustedContacts: updatedAllTrustedContacts},
      () => {
        if(clientMissionsEnabled()) {
          try{
            getClientMissions();
          } catch(e) {
            //do nothing
          }
        }
      },
      () => {Store.dispatch(errorUpdateTrustedContacts())});

    this.setState({
      updatingContacts: true
    });
  };

  onAccountGroupChange = (accountId) => {
    let { accountIdMap } = this.state;
    const accountState = Boolean(accountIdMap[accountId]);
    accountIdMap[accountId] = !accountState;
    this.setState({ accountIdMap });
  };

  onAddressChange = (key, value) => this.onChange('mailingAddress', key)(value);

  onChange = (parentKey, childKey) => (value) => {
    let newChildState;
    if (childKey) {
      const childState = this.state[parentKey];
      newChildState = {
        ...childState,
        [childKey]: value
      };
    } else {
      newChildState = value;
    }

    this.setState({ [parentKey]: newChildState });
  };

  onTrustedContactChange = (accountId, contact) => {
    const { active: selectedActive, sequenceNumber: selectedSequenceNumber } =
      contact;
    let { accountIdTrustedContactsMap } = this.state;
    const accountTrustedContacts = accountIdTrustedContactsMap[accountId];

    if (accountTrustedContacts.length === 1) {
      return;
    }

    const updatedAccountTrustedContacts = accountTrustedContacts.reduce(
      (accum, current) => {
        const { sequenceNumber } = current;
        let { active } = current;
        if (sequenceNumber === selectedSequenceNumber) {
          active = !selectedActive;
        } else {
          active = !active;
        }
        return accum.concat({ active, sequenceNumber });
      },
      []
    );

    this.setState({
      accountIdTrustedContactsMap: {
        ...accountIdTrustedContactsMap,
        [accountId]: updatedAccountTrustedContacts
      }
    });
  };

  resetState = () => {
    let { accountIdMap } = this.state;
    const { closeToolTip } = this.props;
    if (closeToolTip) {
      closeToolTip();
    }
    Object.keys(accountIdMap).forEach(
      (accountId) => (accountIdMap[accountId] = false)
    );
    const accountIdTrustedContactsMap = this.buildAccountIdTrustedContactsMap();
    Store.dispatch(clearInvalidFields());
    Store.dispatch(clearTouchedFields());
    this.setState({
      accountIdMap,
      accountIdTrustedContactsMap,
      showModal: false,
      showTrustedContactsGlobal: false,
      updatingContacts: false,
      ...this.copyBffState()
    });
  };

  showModal = () => {
    this.setState({ showModal: true });
  };

  shouldDisableNext = () => {
    const { invalidFields } = this.props;
    const phoneFields = [
      'daytimePhone',
      'daytimePhoneExt',
      'eveningPhone',
      'eveningPhoneExt',
      'mobilePhone'
    ];
    const requiredNameFields = ['firstName', 'lastName'];
    const validFields = [
      'addressOne',
      'addressTwo',
      'city',
      'emailAddress',
      'relationship',
      'state',
      'zipCode',
      'zipCodeExt',
      ...phoneFields,
      ...requiredNameFields
    ];

    const allRequiredNameFieldsNonEmpty = requiredNameFields.every(
      (field) => this.state[field] !== ''
    );
    const atLeastOnePhoneisNonEmpty = phoneFields.some(
      (field) => this.state['phoneNumbers'][field] !== ''
    );

    const allFieldsValid = checkAllFieldsValid(validFields, invalidFields)

    return (
      !allRequiredNameFieldsNonEmpty ||
      !allFieldsValid ||
      !atLeastOnePhoneisNonEmpty
    );
  };

  shouldDisableSave = () => {
    const { accountIdMap } = this.state;
    const { isAddView, touchedFields } = this.props;

    let hasAtLeastOneActiveAccount;
    if (!isAddView) {
      hasAtLeastOneActiveAccount = !this.shouldDisableNext();
    } else {
      hasAtLeastOneActiveAccount = Object.values(accountIdMap).some(
        (isActive) => isActive
      );
    }

    return !hasAtLeastOneActiveAccount || isEmpty(touchedFields);
  };

  buildAccountIdMap() {
    const { trustedContactMappedGroups } = this.props;

    return trustedContactMappedGroups.reduce((accum, current) => {
      const { name: { accountId } = {} } = current;
      accum[accountId] = false;
      return accum;
    }, {});
  }

  buildAccountIdTrustedContactsMap() {
    const { trustedContactMappedGroups } = this.props;

    return trustedContactMappedGroups.reduce((accum, current) => {
      const { name: { accountId } = {} } = current;
      let { trustedContacts } = current;
      if (trustedContacts === null) {
        accum[accountId] = [{ sequenceNumber: null, active: true }];
      } else {
        const hasSingleTrustedContact = trustedContacts.length === 1;
        accum[accountId] = trustedContacts.map((contact, index) => ({
          sequenceNumber: contact.sequenceNumber,
          active: !hasSingleTrustedContact && index === 0
        }));

        if (hasSingleTrustedContact) {
          accum[accountId].push({
            sequenceNumber: null,
            active: true
          });
        }
      }
      return accum;
    }, {});
  }

  copyBffState() {
    const { account: { name, trustedContacts } = {} } = this.props;
    let { contact } = this.props;

    contact = contact === null ? trustedContactInitialState : contact;
    let { emailAddress, mailingAddress, phoneNumber } = contact;
    const phoneNumbers =
      appPhoneNumberToComponentPhoneNumberMapper(phoneNumber);
    emailAddress = { ...defaultEmailAddressAppState, ...emailAddress };
    mailingAddress = appAddressToComponentAddressMapper({
      ...mailingAddress,
      ...defaultMailingAddressAppState
    });

    return {
      ...pick(contact, [
        'firstName',
        'lastName',
        'relationship',
        'sequenceNumber'
      ]),
      emailAddress,
      mailingAddress,
      name,
      phoneNumbers,
      trustedContacts
    };
  }

  handleDelete = () => this.setState({ showDelete: true });

  handleCloseDelete = () => this.setState({ showDelete: false });

  render() {
    const {
      accountIdMap,
      accountIdTrustedContactsMap,
      emailAddress,
      firstName,
      showModal,
      lastName,
      mailingAddress,
      name,
      phoneNumbers,
      relationship,
      showDelete,
      showTrustedContactsGlobal,
      updatingContacts
    } = this.state;

    const {
      account,
      contact,
      trustedContactMappedGroups,
      isAddView,
      openModal,
      isMobileLayout,
      linkName
    } = this.props;
    const modalTitle = updatingContacts ? ' ' : this.props.modalTitle;
    const currentName = `${firstName} ${lastName}`;
    const disableNext = this.shouldDisableNext();
    const disableSave = this.shouldDisableSave();

    return (
      <div className="trusted-contacts-modal-container">
        <LinkButton
          className="trusted-contacts-link-button"
          onClick={this.showModal}>
          {linkName}
        </LinkButton>

        {(showModal || openModal) && (
          <BaseModal
            className="trusted-contacts-modal-base"
            closeTitle="Cancel"
            disableClickOff={true}
            hideAnchor={true}
            hideClose={true}
            isOpen={showModal || openModal}
            title={modalTitle}>
            {isMobileLayout && !isAddView && (
              <div className="trusted-contacts-modal-mobile-header-container">
                <div className="trusted-contacts-modal-mobile-header">
                  {modalTitle}
                </div>
                <LinkButton
                  className="trusted-contacts-link-button"
                  onClick={this.handleDelete}
                  withPermissions
                  shouldDisable>
                  Delete
                </LinkButton>
              </div>
            )}
            {showDelete && (
              <TrustedContactsDeleteModal
                account={account}
                contact={contact}
                onClose={this.handleCloseDelete}
              />
            )}
            {!updatingContacts && (
              <Fragment>
                {!showTrustedContactsGlobal && (
                  <Fragment>
                    {name && (
                      <div className="trusted-contacts-modal-account-details">
                        <AccountDisplay account={name} withType={true} />
                      </div>
                    )}
                    <div className="trusted-contacts-modal-forms">
                      <div className="trusted-contacts-modal-row">
                        <div className="trusted-contacts-modal-row-label">
                          Required Fields
                        </div>
                        <div className="trusted-contacts-modal-contact-info-form">
                          <ContactInformation
                            firstName={firstName}
                            lastName={lastName}
                            onChange={this.onChange}
                            phoneNumbers={phoneNumbers}
                          />
                        </div>
                      </div>
                      <div className="trusted-contacts-modal-row">
                        <div className="trusted-contacts-modal-row-label">
                          Address
                        </div>
                        <div className="trusted-contacts-modal-address-form">
                          <USAddress
                            address={mailingAddress}
                            onChange={this.onAddressChange}
                            validations={trustedContactAddressValidation}
                          />
                        </div>
                      </div>
                      <div className="trusted-contacts-modal-row">
                        <div className="trusted-contacts-modal-row-label">
                          Other
                        </div>
                        <div className="trusted-contacts-modal-misc-form">
                          <OtherInformation
                            emailAddress={emailAddress}
                            onChange={this.onChange}
                            relationship={relationship}
                          />
                        </div>
                      </div>
                    </div>
                  </Fragment>
                )}
                {showTrustedContactsGlobal && (
                  <TrustedContactsGlobal
                    accountIdMap={accountIdMap}
                    accountIdTrustedContactsMap={accountIdTrustedContactsMap}
                    currentName={currentName}
                    handleClearAll={this.handleClearAll}
                    handleSelectAll={this.handleSelectAll}
                    onAccountGroupChange={this.onAccountGroupChange}
                    onTrustedContactChange={this.onTrustedContactChange}
                    trustedContactMappedGroups={trustedContactMappedGroups}
                  />
                )}

                <div className="trusted-contacts-modal-button-group">
                  {showTrustedContactsGlobal && isAddView && (
                    <button
                      className="button trusted-contacts-modal-back"
                      onClick={this.handleBackClick}>
                      Back
                    </button>
                  )}
                  <button
                    className="button cancel-button"
                    onClick={this.handleCancelClick}>
                    Cancel
                  </button>

                  {!showTrustedContactsGlobal && isAddView && (
                    <button
                      className="button trusted-contacts-modal-next"
                      disabled={disableNext}
                      onClick={this.handleNextClick}>
                      Next
                    </button>
                  )}

                  {((showTrustedContactsGlobal && isAddView) || !isAddView) && (
                    <TrustedContactsButtonWithPermissions
                      className="button trusted-contacts-modal-save"
                      disable={disableSave}
                      onClick={this.handleSaveTrustedContacts}
                      text="Save"
                      shouldDisable
                    />
                  )}
                </div>
              </Fragment>
            )}
            {updatingContacts && (
              <ModalLoadingSpinner label="Updating Trusted Contacts" />
            )}
          </BaseModal>
        )}
      </div>
    );
  }
}

TrustedContactsModal.propTypes = {
  account: object,
  closeToolTip: func,
  contact: object,
  isAddView: bool,
  modalTitle: string.isRequired
};

const mapStateToProps = (state) => ({
  invalidFields: state.invalidFields,
  isMobileLayout: isMobileLayout(state),
  trustedContactMappedGroups: getTrustedContactMappedGroups(state),
  customerTrustedContacts: getCustomerTrustedContacts(state),
  requestStatus: state.requestStatus,
  touchedFields: state.touchedFields,
  accountsById: getAccountsById(state)
});

export default connect(mapStateToProps)(TrustedContactsModal);
